import type { Role } from './tenancy.js';

/* ------------------------------------------------------------------ *
 * Permissions
 *
 * Flat `subject:action` strings, not a policy engine. Two reasons:
 * the strings are greppable a year from now, and they serialise into
 * a JWT without a serialiser. Row-level rules (a driver sees only
 * their own route) are enforced server-side; the client only decides
 * what to render.
 * ------------------------------------------------------------------ */

export const SUBJECTS = [
  'order',
  'market',
  'purchase',
  'shipment',
  'product',
  'price',
  'stock',
  'contact',
  'thread',
  'appointment',
  'invoice',
  'payment',
  'analytics',
  'member',
  'business',
] as const;
export type Subject = (typeof SUBJECTS)[number];

export const ACTIONS = ['read', 'create', 'update', 'delete', 'approve', 'export'] as const;
export type Action = (typeof ACTIONS)[number];

export type Permission = `${Subject}:${Action}` | `${Subject}:*` | '*';

const all = (...subjects: Subject[]): Permission[] => subjects.map((s) => `${s}:*` as Permission);
const read = (...subjects: Subject[]): Permission[] =>
  subjects.map((s) => `${s}:read` as Permission);

/**
 * Role → permissions. Deliberately verbose: when someone asks in
 * March "why can't the buyer see debt", the answer is one table.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  owner: ['*'],

  manager: [
    ...all(
      'order', 'market', 'purchase', 'shipment', 'product', 'price',
      'stock', 'contact', 'thread', 'appointment', 'invoice', 'payment',
    ),
    'analytics:read',
    'analytics:export',
    'member:read',
    'business:read',
  ],

  sales: [
    ...all('order', 'thread', 'appointment'),
    ...read('product', 'stock', 'shipment', 'invoice', 'market'),
    'contact:read',
    'contact:create',
    'contact:update',
    'price:read',
    'price:update', // personal prices are the sales lever
    'analytics:read',
    'business:read',
  ],

  buyer: [
    ...all('market', 'purchase'),
    ...read('order', 'product', 'stock', 'contact', 'invoice'),
    'price:read',
    'price:update',
    'product:create',
    'product:update',
    'thread:read',
    'thread:create',
    'business:read',
  ],

  driver: [
    ...read('shipment', 'order', 'contact'),
    'shipment:update', // mark delivered, record shortfall at the door
    'thread:read',
    'thread:create',
    'business:read',
  ],

  warehouse: [
    ...all('stock'),
    ...read('order', 'product', 'shipment', 'purchase'),
    'order:update', // record shortfall and substitutions while picking
    'shipment:update',
    'business:read',
  ],

  accountant: [
    ...all('invoice', 'payment'),
    ...read('order', 'contact', 'shipment', 'product', 'purchase'),
    'analytics:read',
    'analytics:export',
    'business:read',
  ],

  viewer: [
    ...read(
      'order', 'market', 'purchase', 'shipment', 'product', 'price',
      'stock', 'contact', 'appointment', 'invoice', 'payment', 'analytics',
    ),
    'business:read',
  ],
};

export interface Grant {
  role: Role;
  extraPermissions: string[];
}

/** Expand a membership into a lookup set once, at session load. */
export const grantSet = (grant: Grant): ReadonlySet<string> => {
  const set = new Set<string>(ROLE_PERMISSIONS[grant.role]);
  for (const p of grant.extraPermissions) set.add(p);
  return set;
};

export const can = (
  granted: ReadonlySet<string>,
  subject: Subject,
  action: Action,
): boolean =>
  granted.has('*') || granted.has(`${subject}:*`) || granted.has(`${subject}:${action}`);
